import ApiClient from '@/api-client';
import SseClient from '@/libs/sse-client';

const sseClient = new SseClient();

export default {
    namespaced: true,
    state: {
        isOpen: localStorage.getItem('ai_chat_open') === 'true',
        viewMode: localStorage.getItem('ai_chat_view_mode') || 'overlay',
        conversations: [],
        activeConversationId: parseInt(localStorage.getItem('ai_chat_conversation_id'), 10) || null,
        messages: [],
        isLoadingConversations: false,
        isLoadingMessages: false,
        isMinimized: false,
        isStreaming: false,
        isToolProcessing: false,
        currentToolName: '',
        streamingContent: '',
    },
    getters: {
        activeConversation(state) {
            return state.conversations.find((c) => c.id === state.activeConversationId) || null;
        },
    },
    mutations: {
        setOpen(state, isOpen) {
            state.isOpen = isOpen;
            localStorage.setItem('ai_chat_open', isOpen ? 'true' : 'false');
        },
        setViewMode(state, mode) {
            state.viewMode = mode;
            localStorage.setItem('ai_chat_view_mode', mode);
        },
        setConversations(state, conversations) {
            state.conversations = conversations;
        },
        setActiveConversationId(state, id) {
            state.activeConversationId = id;

            if (id) {
                localStorage.setItem('ai_chat_conversation_id', id);
            } else {
                localStorage.removeItem('ai_chat_conversation_id');
            }
        },
        setMessages(state, messages) {
            state.messages = messages;
        },
        addMessage(state, message) {
            state.messages.push(message);
        },
        setIsMinimized(state, value) {
            state.isMinimized = value;
        },
        setIsLoadingConversations(state, value) {
            state.isLoadingConversations = value;
        },
        setIsLoadingMessages(state, value) {
            state.isLoadingMessages = value;
        },
        setIsStreaming(state, value) {
            state.isStreaming = value;
        },
        setIsToolProcessing(state, value) {
            state.isToolProcessing = value;
        },
        setCurrentToolName(state, name) {
            state.currentToolName = name;
        },
        setStreamingContent(state, content) {
            state.streamingContent = content;
        },
        prependConversation(state, conversation) {
            state.conversations.unshift(conversation);
        },
        updateConversationTitle(state, { id, title }) {
            const conversation = state.conversations.find((c) => c.id === id);

            if (conversation) {
                conversation.title = title;
            }
        },
        removeConversation(state, id) {
            state.conversations = state.conversations.filter((c) => c.id !== id);
        },
    },
    actions: {
        async fetchConversations({ commit }) {
            commit('setIsLoadingConversations', true);

            try {
                const conversations = await ApiClient.getCollection('/api/ai-chat/conversations');
                commit('setConversations', conversations);
            } finally {
                commit('setIsLoadingConversations', false);
            }
        },

        async fetchMessages({ commit, dispatch }, conversationId) {
            commit('setIsLoadingMessages', true);
            commit('setActiveConversationId', conversationId);

            try {
                const response = await ApiClient.get(`/api/ai-chat/conversations/${conversationId}`);
                commit('setMessages', response.data.messages || []);
            } catch (e) {
                if (e.response && e.response.status === 404) {
                    dispatch('startNewConversation');
                }
            } finally {
                commit('setIsLoadingMessages', false);
            }
        },

        async sendMessage({ commit, state }, payload) {
            // Handle both old format (string) and new format (object with text, context, images)
            const messageText = typeof payload === 'string' ? payload : payload.text;
            const context = typeof payload === 'string' ? null : payload.context;
            const images = typeof payload === 'string' ? [] : (payload.images || []);

            const userMessage = {
                role: 'user',
                content: messageText,
                created_at: new Date().toISOString(),
            };

            if (images.length) {
                userMessage.images = images.map((img) => ({ preview: img.preview }));
            }

            commit('addMessage', userMessage);

            commit('setIsStreaming', true);
            commit('setStreamingContent', '');

            const requestData = {
                message: messageText,
                conversation_id: state.activeConversationId,
            };

            if (context) {
                requestData.context = context;
            }

            if (images.length) {
                requestData.images = images.map((img) => ({
                    data: img.data,
                    media_type: img.media_type,
                }));
            }

            let lastMessageId = null;

            await sseClient.stream(
                '/api/ai-chat/send',
                requestData,
                {
                    onToken(token, fullText) {
                        commit('setIsToolProcessing', false);
                        commit('setCurrentToolName', '');
                        commit('setStreamingContent', fullText);
                    },
                    onDone(fullText, meta) {
                        const content = meta?.incomplete
                            ? `${fullText}\n\n---\n*Response may be incomplete — connection was interrupted.*`
                            : fullText;

                        commit('addMessage', {
                            id: lastMessageId,
                            role: 'assistant',
                            content: content || 'Sorry, something went wrong. Please try again.',
                            isError: !fullText,
                            created_at: new Date().toISOString(),
                        });
                        commit('setStreamingContent', '');
                        commit('setIsStreaming', false);
                        commit('setIsToolProcessing', false);
                        commit('setCurrentToolName', '');
                    },
                    onError(error) {
                        commit('addMessage', {
                            role: 'assistant',
                            content: error?.message || 'Sorry, something went wrong. Please try again.',
                            isError: true,
                            created_at: new Date().toISOString(),
                        });
                        commit('setStreamingContent', '');
                        commit('setIsStreaming', false);
                        commit('setIsToolProcessing', false);
                        commit('setCurrentToolName', '');
                    },
                    onEvent(event) {
                        if (event.type === 'conversation_started') {
                            commit('setActiveConversationId', event.conversation_id);
                            commit('prependConversation', {
                                id: event.conversation_id,
                                title: messageText.substring(0, 50),
                                created_at: new Date().toISOString(),
                            });
                        } else if (event.type === 'tool_use') {
                            commit('setIsToolProcessing', true);
                            commit('setCurrentToolName', event.name || '');
                        } else if (event.type === 'message_complete') {
                            lastMessageId = event.message_id;
                        }
                    },
                },
            );
        },

        async startNewConversation({ commit }) {
            commit('setActiveConversationId', null);
            commit('setMessages', []);
        },

        async sendFeedback({ state }, { messageId, feedback }) {
            await ApiClient.post(
                `/api/ai-chat/conversations/${state.activeConversationId}/messages/${messageId}/feedback`,
                { feedback },
            );

            const message = state.messages.find((m) => m.id === messageId);

            if (message) {
                message.feedback = feedback;
            }
        },

        async renameConversation({ commit }, { id, title }) {
            await ApiClient.patch(`/api/ai-chat/conversations/${id}`, { title });
            commit('updateConversationTitle', { id, title });
        },

        async deleteConversation({ commit, state, dispatch }, conversationId) {
            await ApiClient.delete(`/api/ai-chat/conversations/${conversationId}`);
            commit('removeConversation', conversationId);

            if (state.activeConversationId === conversationId) {
                dispatch('startNewConversation');
            }
        },

        async regenerateLastMessage({ commit, state }) {
            // Remove the last assistant message from local state
            const lastIndex = [...state.messages].reverse().findIndex((m) => m.role === 'assistant');

            if (lastIndex !== -1) {
                const actualIndex = state.messages.length - 1 - lastIndex;
                state.messages.splice(actualIndex, 1);
            }

            commit('setIsStreaming', true);
            commit('setStreamingContent', '');

            let regenMessageId = null;

            await sseClient.stream(
                `/api/ai-chat/conversations/${state.activeConversationId}/regenerate`,
                {},
                {
                    onToken(token, fullText) {
                        commit('setIsToolProcessing', false);
                        commit('setCurrentToolName', '');
                        commit('setStreamingContent', fullText);
                    },
                    onDone(fullText, meta) {
                        const content = meta?.incomplete
                            ? `${fullText}\n\n---\n*Response may be incomplete — connection was interrupted.*`
                            : fullText;

                        commit('addMessage', {
                            id: regenMessageId,
                            role: 'assistant',
                            content: content || 'Sorry, something went wrong. Please try again.',
                            isError: !fullText,
                            created_at: new Date().toISOString(),
                        });
                        commit('setStreamingContent', '');
                        commit('setIsStreaming', false);
                        commit('setIsToolProcessing', false);
                        commit('setCurrentToolName', '');
                    },
                    onError(error) {
                        commit('addMessage', {
                            role: 'assistant',
                            content: error?.message || 'Sorry, something went wrong. Please try again.',
                            isError: true,
                            created_at: new Date().toISOString(),
                        });
                        commit('setStreamingContent', '');
                        commit('setIsStreaming', false);
                        commit('setIsToolProcessing', false);
                        commit('setCurrentToolName', '');
                    },
                    onEvent(event) {
                        if (event.type === 'tool_use') {
                            commit('setIsToolProcessing', true);
                            commit('setCurrentToolName', event.name || '');
                        } else if (event.type === 'message_complete') {
                            regenMessageId = event.message_id;
                        }
                    },
                },
            );
        },

        abortStream() {
            sseClient.abort();
        },

        openConversation({ dispatch }, conversationId) {
            dispatch('fetchMessages', conversationId);
        },

        close({ commit }) {
            commit('setOpen', false);
        },
    },
};
